import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Link as MuiLink,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { 
  Email, 
  Lock, 
  Security, 
  CheckCircle, 
  Visibility, 
  VisibilityOff 
} from '@mui/icons-material';
import { useAuth } from './AuthContext';

const ForgotPassword = ({ onStateChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [username, setUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const { forgotPassword, forgotPasswordSubmit } = useAuth();

  const validateEmailForm = () => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'Please enter your email';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateResetForm = () => {
    const errors = {};
    
    if (!formData.code) {
      errors.code = 'Please enter the reset code';
    } else if (formData.code.length !== 6) {
      errors.code = 'Reset code must be 6 digits';
    }
    
    if (!formData.newPassword) {
      errors.newPassword = 'Please enter your new password';
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.newPassword)) {
      errors.newPassword = 'Password must contain uppercase, lowercase, number, and symbol';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    if (!validateEmailForm()) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await forgotPassword(formData.email);
      setUsername(formData.email);
      setCurrentStep(1);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!validateResetForm()) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await forgotPasswordSubmit(username, formData.code, formData.newPassword);
      setCurrentStep(2);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box component="form" onSubmit={handleRequestCode} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!formErrors.email}
              helperText={formErrors.email}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                height: 48,
                fontSize: '1rem',
                mb: 2
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Reset Code'
              )}
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We've sent a reset code to <strong>{username}</strong>
            </Typography>

            <TextField
              fullWidth
              label="Reset Code"
              value={formData.code}
              onChange={handleChange('code')}
              error={!!formErrors.code}
              helperText={formErrors.code}
              inputProps={{ maxLength: 6 }}
              sx={{ 
                mb: 2,
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  letterSpacing: '0.5em'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Security color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleChange('newPassword')}
              error={!!formErrors.newPassword}
              helperText={formErrors.newPassword}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                height: 48,
                fontSize: '1rem',
                mb: 2
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Reset Password'
              )}
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" component="h3" gutterBottom color="success.main" fontWeight="bold">
              Password Reset Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your password has been successfully reset. You can now sign in with your new password.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => onStateChange('signIn')}
              sx={{ height: 48, fontSize: '1rem' }}
              fullWidth
            >
              Sign In
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  const stepTitles = ['Enter Email', 'Reset Password', 'Complete'];

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          py: 3
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom color="primary" fontWeight="bold">
                Reset Password
              </Typography>
              <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 2 }}>
                {stepTitles.map((title, index) => (
                  <Step key={index}>
                    <StepLabel>{title}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  Reset Failed
                </Typography>
                {error}
              </Alert>
            )}

            {renderStepContent()}

            {currentStep < 2 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2">
                  <MuiLink
                    component="button"
                    variant="body2"
                    onClick={() => onStateChange('signIn')}
                    sx={{ textDecoration: 'none', fontWeight: 500 }}
                  >
                    Back to sign in
                  </MuiLink>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ForgotPassword;